import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { debounce } from "lodash";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

import { Grid, MapControls, OrbitControls, PerspectiveCamera, Text } from "@react-three/drei";
import "./index.css";

import { useContext } from "react";
import { Store } from "../../store";

import { gsap } from "gsap";
import { toast } from "react-toastify";

import useQueryParams from "../../hooks/useQueryParams";
import { getProducts } from "../../services/inventoryService";
import { getWarehouseLayout, updateLayout } from "../../services/warehouseLayoutService";
import AppModal from "../../shared/components/app-modal/AppModal";
import { UNIT_TYPES } from "../../utils/constant";
import { eventEmiterInstance } from "../../utils/EventEmitter";
import { highlightLocations } from "../../utils/helpers";
import { binInfo } from "../../utils/visualizerUtils";
import ConfigureShelf from "../ConfigureShelf/ConfigureShelf";
import BinDetails from "./BinDetails/BinDetails";
import AreaWrapper from "./components/Area";
import BinDetailModal from "./components/BinDetailModal/BinDetailModal";
import BinLabel from "./components/BinLabel/BinLabel";
import CameraControls from "./components/CameraControls/CameraControls";
import DirectionIndicator from "./components/DirectionIndicator/DirectionIndicator";
import ModernDock from "./components/Dock";
import InfoPanel from "./components/InfoPanel/InfoPanel";
import InteractiveObject from "./components/InteractiveObject/InteractiveObject";
import LayoutObjectContextMenu from "./components/LayoutObjectContextMenu/LayoutObjectContextMenu";
import ObjectInfoPanel from "./components/ObjectInfoPanel/ObjectInfoPanel";
import Rack from "./components/Rack";
import ScaleFactor from "./components/ScaleFactor/ScaleFactor";
import ModernWall from "./components/Wall";
import Zone from "./components/Zone";
import { calculateFontSize, getWallHeight } from "./utils";

const Camera = ({ cameraView, setCamera }) => {
  const cameraRef = useRef();
  const { set, camera } = useThree();
  const urlParams = useQueryParams();
  useFrame(() => {
    const dir = new THREE.Vector3();
    const sph = new THREE.Spherical();

    const camera = cameraRef.current;
    if (camera) {
      const bottomLimit = 0; // Adjust this value to set the bottom limit
      if (camera.position.y < bottomLimit) {
        camera.position.y = bottomLimit;
      }

      cameraRef.current.getWorldDirection(dir);
      sph.setFromVector3(dir);

      eventEmiterInstance.emit("cameraPositionUpdate", {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z,
        radius: sph.radius,
        phi: sph.phi,
        theta: sph.theta,
      });
    }
  });

  // //useEffect to smoothly update the camera position whenever the cameraView value changes.
  useEffect(() => {
    if (cameraRef.current && cameraView && urlParams.binLocation) {
      // status.current = true
      // const camera = cameraRef.current;
      gsap.to(cameraRef.current.position, {
        x: cameraView?.[0],
        y: cameraView?.[1],
        z: cameraView?.[2],
        duration: 2,
        delay: 0.3,
        ease: "sine.inOut",
      });
    }
  }, [cameraView]);

  useEffect(() => {
    if (setCamera) {
      setCamera(cameraRef);
    }
  }, [cameraRef, setCamera]);

  return <PerspectiveCamera ref={cameraRef} makeDefault position={[-1.74, 314, 522]} aspect={window.innerWidth / window.innerHeight} fov={50} />;
};

// --- Constants ---
const BOX_GEOMETRY = new THREE.BoxGeometry(1, 1, 1);
// const HIGHLIGHT_COLOR = new THREE.Color("#0BDA51"); // Greenish highlight
const HIGHLIGHT_COLOR = new THREE.Color("#ff0000"); // Greenish highlight

// Helper function to create a scaled and positioned matrix
const getMatrix = (position, size, targetMatrix) => {
  targetMatrix.identity();
  targetMatrix.makeScale(size[0], size[1], size[2]);
  targetMatrix.setPosition(position[0], position[1], position[2]);
  return targetMatrix;
};

const GradientMaterial = ({ colors, transparent, opacity }) => {
  const texture = useMemo(() => {
    // Only create gradient for multiple colors
    if (!colors || colors.length <= 1) return null;

    try {
      const canvas = document.createElement("canvas");
      canvas.width = 64; // Small texture for performance
      canvas.height = 64;
      const context = canvas.getContext("2d");

      // Create gradient
      const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
      const step = 1 / (colors.length - 1);

      colors.forEach((color, index) => {
        const colorStop = index * step;
        if (isFinite(colorStop)) {
          gradient.addColorStop(colorStop, color);
        }
      });

      context.fillStyle = gradient;
      context.fillRect(0, 0, canvas.width, canvas.height);

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      return texture;
    } catch (error) {
      console.error("Error creating gradient texture:", error);
      return null;
    }
  }, [colors]);

  // Use gradient texture if available
  if (texture) {
    return <meshBasicMaterial map={texture} transparent={transparent} opacity={opacity || 1} side={THREE.DoubleSide} depthTest={true} depthWrite={false} />;
  }

  // Fallback to single color
  const fallbackColor = colors && colors.length > 0 ? colors[0] : "#0BDA51";
  return <meshBasicMaterial color={fallbackColor} transparent={transparent} opacity={opacity || 1} side={THREE.DoubleSide} />;
};

const InstancedCube = ({
  instances = [],
  showLabels,
  rackInfo,
  rackId,
  getRemainingRackIds,
  resetRackInstances,
  highlightAreas,
  updateCube,
  rotation = 0,
  isWareHouseInventoryRoute,
  showBinDetailsDirectly,
  onOpenContextMenu,
}) => {
  const { state, dispatch } = useContext(Store);

  // --- Refs ---
  const meshRef = useRef();
  const edgesRef = useRef([]);
  const selectedInstanceRef = useRef(null);
  const groupRef = useRef(); // Add group ref for rotated racks

  // --- Hooks ---
  const { invalidate } = useThree();

  // --- State ---
  const [isRackSelected, setIsRackSelected] = useState(null);
  const [areBinsVisible, setAreBinsVisible] = useState(true);
  const [gradientInstances, setGradientInstances] = useState([]);
  // Add popup state
  const [selectedBin, setSelectedBin] = useState(null);

  // --- Memoized Values ---
  const rayCaster = useMemo(() => new THREE.Raycaster(), []);
  const mouse = useMemo(() => new THREE.Vector2(), []);
  const tempMatrix = useMemo(() => new THREE.Matrix4(), []);
  const tempPosition = useMemo(() => new THREE.Vector3(), []);
  const originalColor = useMemo(() => new THREE.Color(rackInfo.color), [rackInfo?.color]);
  const edgesGeometry = useMemo(() => new THREE.EdgesGeometry(BOX_GEOMETRY), []);
  const edges = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)), []);

  // --- Route Checks ---
  const isInventoryRoute = useMemo(() => window.location.pathname.split("/").some((path) => ["warehouse-inventory"].includes(path)), []);

  // Helper function to create gradient texture
  const createGradientTexture = useCallback((colors) => {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 256;
    const context = canvas.getContext("2d");

    const gradient = context.createLinearGradient(0, 0, 256, 256);
    const step = 1 / (colors.length - 1);

    colors.forEach((color, index) => {
      gradient.addColorStop(index * step, color);
    });

    context.fillStyle = gradient;
    context.fillRect(0, 0, 256, 256);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }, []);

  // Function to get color based on tags
  const getColorFromTags = useCallback(
    (tags) => {
      if (!tags || tags.length === 0) {
        return HIGHLIGHT_COLOR;
      }

      if (tags.length === 1) {
        return new THREE.Color(tags[0].color || HIGHLIGHT_COLOR);
      }

      const colors = tags.filter((tag) => tag.color !== null && tag.color !== undefined).map((tag) => tag.color);
      return createGradientTexture(colors);
    },
    [createGradientTexture],
  );

  // Create materials for each instance
  const materials = useMemo(() => {
    return instances.map((instance) => {
      if (instance.tags && instance.tags.length > 1) {
        return (
          <GradientMaterial
            key={`material-${instance.code}`}
            colors={instance.tags.filter((tag) => tag.color !== null && tag.color !== undefined).map((tag) => tag.color)}
            transparent={false}
            opacity={1}
          />
        );
      } else {
        const color = instance.tags && instance.tags.length === 1 ? instance.tags[0].color : instance.color || rackInfo.color;
        return <meshBasicMaterial color={color} />;
      }
    });
  }, [instances, rackInfo.color]);

  // --- Event Handlers ---
  const hideExternalRacks = useCallback(
    (codes) => {
      if (!meshRef.current) return;

      // If codes is null or empty, reset all instances
      if (!codes) {
        setAreBinsVisible(false);
        setGradientInstances([]);
        highlightAreas([], null);
        resetRackInstances();
        invalidate();
        return;
      }

      const { locationCodes = [], primaryColor } = codes;
      const areaLocations = locationCodes || [];
      const isAnyInstanceMatch = instances.some((instance) => locationCodes.includes(instance.code));
      setAreBinsVisible(isAnyInstanceMatch);

      const zeroScale = [0, 0, 0];
      const zeroPosition = [0, 0, 0];

      const newGradientInstances = [];

      // Update all instances
      for (let i = 0; i < instances.length; i++) {
        const instance = instances[i];
        instance.tags = instance?.tags?.filter((tag) => tag.color !== null && tag.color !== undefined) || [];
        const isCodeMatch = locationCodes.includes(instance.code);
        const shouldRender = isCodeMatch || isAnyInstanceMatch;

        // Get color based on tags or default
        let targetColor;

        if (isCodeMatch && instance.tags && instance.tags.length > 0) {
          targetColor = getColorFromTags(instance.tags);
          newGradientInstances.push({
            ...instance,
            instanceId: i,
            colors: instance.tags.filter((tag) => tag.color !== null && tag.color !== undefined).map((tag) => tag.color),
          });
        } else {
          targetColor = isCodeMatch ? (primaryColor ? new THREE.Color(primaryColor) : HIGHLIGHT_COLOR) : originalColor;
        }

        const targetSize = shouldRender ? instance.size : zeroScale;
        const targetPosition = shouldRender ? instance.position : zeroPosition;

        // Update instance color or texture
        if (targetColor instanceof THREE.Texture) {
          meshRef.current.setColorAt(i, HIGHLIGHT_COLOR);
        } else {
          meshRef.current.setColorAt(i, targetColor);
        }

        const hexCode = targetColor instanceof THREE.Color ? `#${targetColor.getHexString()}` : "gradient";
        instances[i].color = hexCode;

        // Update instance matrix (position and scale)
        getMatrix(targetPosition, targetSize, tempMatrix);
        meshRef.current.setMatrixAt(i, tempMatrix);

        // Update corresponding edge visibility (by scaling)
        if (edgesRef.current[i]) {
          edgesRef.current[i].scale.set(...targetSize);
        }

        if (isCodeMatch) {
          const index = areaLocations.indexOf(instance.code);
          areaLocations.splice(index, 1);
        }
      }

      if (newGradientInstances.length > 0) {
        setGradientInstances(newGradientInstances);
      }

      const remainingRackIds = getRemainingRackIds(rackId);

      if (remainingRackIds.length === 1 || (areaLocations.length > 0 && !isAnyInstanceMatch)) {
        highlightAreas(areaLocations, primaryColor);
        resetRackInstances();
      } else {
        resetRackInstances();
      }

      if (areaLocations?.length < 1) {
        highlightAreas([], null);
      } else if (areaLocations?.length > 0) {
        highlightAreas(areaLocations, primaryColor);
      }

      meshRef.current.instanceMatrix.needsUpdate = true;
      meshRef.current.instanceColor.needsUpdate = true;
      invalidate();
    },
    [instances, rackInfo?.color, getColorFromTags],
  );

  const handleExternalSelection = useCallback(
    (selection) => {
      if (!meshRef.current) return;

      if (selection && selection.rackId === rackId) {
        setIsRackSelected(true);
      } else if (selectedInstanceRef.current) {
        const instanceColor = new THREE.Color(selectedInstanceRef.current.color);
        meshRef.current?.setColorAt(selectedInstanceRef.current.instanceId, instanceColor);
        meshRef.current.instanceColor.needsUpdate = true;
        selectedInstanceRef.current = null;
        setIsRackSelected(false);
        invalidate();
      }
    },
    [rackId, originalColor],
  );

  const resetAllRacks = useCallback(() => {
    if (!meshRef.current) return;

    setAreBinsVisible(true);
    setIsRackSelected(false);

    for (let i = 0; i < instances.length; i++) {
      const instance = instances[i];

      getMatrix(instance.position, instance.size, tempMatrix);
      meshRef.current.setMatrixAt(i, tempMatrix);
      meshRef.current.setColorAt(i, originalColor);

      instances[i].color = `#${originalColor.getHexString()}`;

      if (edgesRef.current[i]) {
        edgesRef.current[i].scale.set(...instance.size);
      }
    }

    meshRef.current.instanceMatrix.needsUpdate = true;
    meshRef.current.instanceColor.needsUpdate = true;

    if (selectedInstanceRef.current) {
      selectedInstanceRef.current = null;
    }

    const remainingRackIds = getRemainingRackIds(rackId);
    if (remainingRackIds.length === 1) {
      try {
        updateCube();
      } catch (error) {
        console.log("error", error);
      }
    }

    invalidate();
  }, [instances, originalColor]);

  useEffect(() => {
    eventEmiterInstance.on("selection", handleExternalSelection);
    eventEmiterInstance.on("hideRacks", hideExternalRacks);
    eventEmiterInstance.on("resetAllRacks", resetAllRacks);

    return () => {
      eventEmiterInstance.off("selection", handleExternalSelection);
      eventEmiterInstance.off("hideRacks", hideExternalRacks);
      eventEmiterInstance.off("resetAllRacks", resetAllRacks);
    };
  }, [hideExternalRacks, handleExternalSelection, resetAllRacks]);

  useEffect(() => {
    if (!meshRef.current) return;

    instances.forEach((instance, index) => {
      const { position, size } = instance;

      tempMatrix.identity();
      tempMatrix.makeScale(...size);
      tempPosition.set(position[0], position[1], position[2]);

      tempMatrix.setPosition(tempPosition);
      meshRef.current.setMatrixAt(index, tempMatrix);
      meshRef.current.setColorAt(index, new THREE.Color(instance.color || rackInfo.color));
    });

    meshRef.current.computeBoundingBox();
    meshRef.current.computeBoundingSphere();

    meshRef.current.instanceMatrix.needsUpdate = true;
    meshRef.current.instanceColor.needsUpdate = true;
  }, [instances, rackId]);

  useEffect(() => {
    edgesRef.current.forEach((edge, index) => {
      if (!edge || !instances[index]) return;
      const { position, size = [1, 1, 1] } = instances[index];
      edge.scale.set(...size);
      edge.position.set(...position);
    });
  }, [instances, rackId]);

  const performRaycast = (event) => {
    event.stopPropagation();

    // If R3F provided instanceId in event, we don't need manual raycast
    // but still keep raycast fallback for other cases
    mouse.x = (event.nativeEvent.offsetX / event.nativeEvent.target.offsetWidth) * 2 - 1;
    mouse.y = -(event.nativeEvent.offsetY / event.nativeEvent.target.offsetHeight) * 2 + 1;

    rayCaster.setFromCamera(mouse, event.camera);

    // Prefer intersecting the group (so Three handles group transforms)
    if (groupRef.current) {
      return rayCaster.intersectObject(groupRef.current, true);
    }
    return rayCaster.intersectObject(meshRef.current, true);
  };

  // Edges for the Rack Bins
  useEffect(() => {
    instances.forEach((instance, i) => {
      const edge = edgesRef.current[i];
      if (!edge) return;

      edge.position.set(...instance.position);
      edge.scale.set(...instance.size); // SAME scale as instanced mesh
      edge.updateMatrix();
    });
  }, [instances, edgesRef]);

  const getClickedInstanceData = (event) => {
    if (!event) return;
    try {
      const rf3InstanceId = event.instanceId;
      let instanceId = typeof rf3InstanceId === "number" && rf3InstanceId >= 0 ? rf3InstanceId : null;
      // If no instanceId from event, perform raycast
      if (instanceId === null) {
        const intersections = performRaycast(event);
        if (!intersections || intersections.length === 0) return;
        instanceId = intersections[0].instanceId;
      }

      if (instanceId === null || instanceId >= instances.length) return; // Also handle this case in the method where this is called

      const clickedInstanceData = { ...instances[instanceId], instanceId, rackId };
      return clickedInstanceData;
    } catch (error) {
      console.log(error);
    }
  };

  const handleBinContextMenu = (event) => {
    // event.point is a THREE.Vector3 of the intersection point in world coords
    const worldPoint = event.point.clone();
    // optionally get screen coords:
    const { clientX, clientY } = event;

    // send object and world position to parent
    onOpenContextMenu &&
      onOpenContextMenu({
        object: rackInfo,
        worldPoint, // THREE.Vector3
        screen: { x: clientX, y: clientY },
      });

    event.stopPropagation();
  };

  const handleClick = (event) => {
    const clickedInstanceData = getClickedInstanceData(event);
    if (!clickedInstanceData) return;
    const { instanceId } = clickedInstanceData;

    // Compute instance world position robustly: read instance matrix then apply group's world matrix (if exists)
    const worldPos = new THREE.Vector3();
    const pm = new THREE.Matrix4();

    // get instance matrix
    meshRef.current.getMatrixAt(instanceId, pm);
    // extract local position from instance matrix
    const localPos = new THREE.Vector3();
    localPos.setFromMatrixPosition(pm);

    if (groupRef.current) {
      // convert local position -> world
      worldPos.copy(localPos).applyMatrix4(groupRef.current.matrixWorld);
    } else {
      worldPos.copy(localPos);
    }

    // Selection/deselection logic (unchanged)
    if (!isInventoryRoute && selectedInstanceRef.current?.instanceId === instanceId) {
      const instanceColor = new THREE.Color(selectedInstanceRef.current.color);
      meshRef.current.setColorAt(instanceId, instanceColor);
      meshRef.current.instanceColor.needsUpdate = true;
      selectedInstanceRef.current = null;
      setIsRackSelected(false);
      setSelectedBin(null);
      showBinDetailsDirectly(null);
    } else {
      if (selectedInstanceRef.current) {
        const instanceColor = new THREE.Color(selectedInstanceRef.current.color);
        meshRef.current.setColorAt(selectedInstanceRef.current.instanceId, instanceColor);
        meshRef.current.instanceColor.needsUpdate = true;
      }

      meshRef.current.setColorAt(instanceId, HIGHLIGHT_COLOR);
      meshRef.current.instanceColor.needsUpdate = true;
      selectedInstanceRef.current = clickedInstanceData;
      // setSelectedBin(clickedInstanceData);
      if (!isWareHouseInventoryRoute) {
        showBinDetailsDirectly(clickedInstanceData);
      }

      !isRackSelected && eventEmiterInstance.emit("selection", selectedInstanceRef.current);

      // If inventory route, emit showBinInfo -> bin modal
      if (isRackSelected && isInventoryRoute) {
        eventEmiterInstance.emit("showBinInfo", selectedInstanceRef.current);
      }

      invalidate();
    }

    // Prevent event bubbling in 3D visualizer.
    // Without this, clicks pass through the selected bin and highlight the bin behind it.
    // JIRA: SEP-1501
    event.stopPropagation();
  };

  const closePopup = () => {
    setSelectedBin(null);
  };

  const onBinUpdate = (updatedBin) => {
    const elements = state.elements;
    setSelectedBin(updatedBin);

    // 1. Find the rack that contains this bin
    const rack = elements.find((el) => el.id === updatedBin.rackId);
    if (!rack) return console.error("Rack not found for bin:", updatedBin);

    // 2. Replace the bin inside rack.bins
    const updatedBins = rack.bins.map((b) => (b.id === updatedBin.id ? { ...b, ...updatedBin } : b));

    // 3. Build updated rack
    const updatedRack = {
      ...rack,
      bins: updatedBins,
    };

    // 4. Dispatch the updated element into global state
    dispatch({
      type: "UPDATE_ELEMENT",
      payload: updatedRack,
    });
  };

  const BIN_SIZE = 0.9;
  const LABEL_OFFSET = BIN_SIZE / 2 + 0.01; // tiny offset from surface

  const boxGeo = useMemo(() => new THREE.BoxGeometry(BIN_SIZE, BIN_SIZE, BIN_SIZE), []);
  const edgeGeo = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(BIN_SIZE, BIN_SIZE, BIN_SIZE)), [BIN_SIZE, BIN_SIZE, BIN_SIZE]);

  const rackContent = (
    <>
      {/* <ambientLight intensity={0.35} /> */}
      {/* <directionalLight position={[3, 4, 5]} intensity={0.8} castShadow /> */}
      <instancedMesh ref={meshRef} args={[boxGeo, null, instances.length]} onClick={handleClick} onContextMenu={(e) => handleBinContextMenu(e)}>
        <boxGeometry args={[0.9, 0.9, 0.9]} />
        {/* <meshBasicMaterial color={rackInfo.color} /> */}
        {/* <meshStandardMaterial
          color={"#e4ac71"}
          roughness={0.35}
          metalness={0.15}
          emissive={isRackSelected ? "#774106" : "#000000"}
          emissiveIntensity={isRackSelected ? 0.3 : 0}
        /> */}

        <meshStandardMaterial
          color="#b48756" // Light warehouse blue
          transparent={true}
          opacity={0.85} // Slightly see-through (0.4–0.7 looks nice)
        />
      </instancedMesh>

      {/* Bin Popup */}
      {selectedBin && !isWareHouseInventoryRoute && <BinDetailModal binData={selectedBin} onClose={closePopup} onBinUpdate={onBinUpdate} />}

      {gradientInstances.map((instance) => (
        <mesh
          key={`gradient-${instance.instanceId}`}
          position={instance.position}
          scale={instance.size}
          onClick={(e) => {
            e.stopPropagation();
            handleClick({
              ...e,
              instanceId: instance.instanceId,
              nativeEvent: e.nativeEvent,
              camera: e.camera,
            });
          }}
        >
          <boxGeometry />
          <GradientMaterial colors={instance.colors} transparent={false} opacity={1} />
        </mesh>
      ))}

      {instances.map((instance, index) => {
        return (
          <lineSegments key={index} ref={(el) => (edgesRef.current[index] = el)} matrixAutoUpdate={false}>
            <primitive object={edgeGeo} attach="geometry" />
            <lineBasicMaterial color="#78450f" />
          </lineSegments>
        );
      })}

      {/* {areBinsVisible && <RackLabel key={rackInfo.name} rackInfo={{ ...rackInfo, rotation }} />} */}

      {isRackSelected &&
        areBinsVisible &&
        showLabels &&
        instances.map((instance, index) => (
          <BinLabel key={`${rackId}-${index}`} position={instance.position} size={instance.size} binCode={instance.code} rackInfo={rackInfo} />
        ))}
    </>
  );

  // If there's rotation, wrap in a group with the rotation applied
  if (rotation !== 0) {
    return (
      <group ref={groupRef} position={rackInfo.position} rotation={[0, (-rotation * Math.PI) / 180, 0]}>
        {rackContent}
      </group>
    );
  }

  // If no rotation, return the content directly
  return rackContent;
};

function RackLabel({ key, rackInfo, scaleFactor }) {
  const [selectedRack, setSelectedRack] = useState(null);
  const { camera } = useThree();
  const textRef = useRef();
  const infoPanelRef = useRef();

  // If there are no bins, fallback to rackInfo.position
  const bins = rackInfo?.bins || [];

  // Compute center X/Z from bins (robust for local coords)
  let minX = Infinity,
    maxX = -Infinity,
    minZ = Infinity,
    maxZ = -Infinity,
    topY = -Infinity;
  if (bins.length > 0) {
    bins.forEach((bin) => {
      const [bx, by, bz] = bin.position;
      const [sx, sy, sz] = bin.size;
      // bin extents in local coords
      const left = bx - sx / 2;
      const right = bx + sx / 2;
      const front = bz - sz / 2;
      const back = bz + sz / 2;
      minX = Math.min(minX, left);
      maxX = Math.max(maxX, right);
      minZ = Math.min(minZ, front);
      maxZ = Math.max(maxZ, back);
      // top of this bin (local)
      topY = Math.max(topY, by + sy / 2);
    });
  } else {
    // fallback to rackInfo.position if no bins
    const [rx, ry, rz] = rackInfo.position || [0, 0, 0];
    minX = rx;
    maxX = rx;
    minZ = rz;
    maxZ = rz;
    topY = (rackInfo.size?.[1] || 1) + (ry || 0);
  }

  // center in local/group coordinates
  const centerX = (minX + maxX) / 2;
  const centerZ = (minZ + maxZ) / 2;

  // small upward offset so label sits above boxes; scale if you want responsive offset
  const labelYOffset = Math.max(0.5, (rackInfo.size?.[1] || 1) * 0.1) + 5;
  const labelPosition = [centerX, topY + labelYOffset, centerZ];

  // Always face the camera
  useFrame(() => {
    if (textRef.current) textRef.current.lookAt(camera.position);
    if (infoPanelRef.current) infoPanelRef.current.lookAt(camera.position);
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      const infoPanelElement = infoPanelRef.current;
      if (infoPanelElement && event.target !== infoPanelElement) setSelectedRack(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const onSelectedRackHandler = (r) => setSelectedRack((prev) => (prev?.id === r?.id ? null : r));

  const fontSize = calculateFontSize(rackInfo?.size?.[0] || 1, rackInfo?.size?.[2] || 1, rackInfo.name) + 0.5;

  return (
    <group>
      <Text
        ref={textRef}
        onClick={() => onSelectedRackHandler(rackInfo)}
        position={labelPosition}
        // keep text upright; we rotate label only visually with lookAt
        rotation={[0, 0, 0]}
        key={key}
        fontSize={fontSize}
        fontWeight="bold"
        color="black"
        maxWidth={50}
        lineHeight={1}
        letterSpacing={0}
        textAlign="center"
        renderOrder={999}
        // these props ensure it's always visible on top
        depthTest={false}
        depthWrite={false}
      >
        {rackInfo?.name}
      </Text>

      {selectedRack?.id === rackInfo?.id && (
        <InfoPanel
          selectedRack={selectedRack}
          rackInfo={rackInfo}
          _position={labelPosition}
          infoPanelRef={infoPanelRef}
          rackWidth={rackInfo.width}
          rackLength={rackInfo.length}
        />
      )}
    </group>
  );
}

const SceneReadyNotifier = ({ onReady }) => {
  const hasNotified = useRef(false);
  useFrame(() => {
    if (!hasNotified.current) {
      hasNotified.current = true;
      onReady();
    }
  });
  return null;
};

const MODES = {
  NONE: null,
  PAN: "pan",
  MOVE: "move",
  SELECT: "select",
};

const Visualizer = forwardRef(({ header, setSelectItem, fullscreen }, ref) => {
  const { state, dispatch } = useContext(Store);
  const [objects, setObjects] = useState([]);
  const [previewObjects, setPreviewObjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [areaLocationsToHighlight, setAreaLocationsToHighlight] = useState({});

  //Scale Factor Loading
  const [scaleFactorLoading, setScaleFactorLoading] = useState(false);

  // Camera State for ZoomControls
  const [camera, setCamera] = useState(null);

  const mapControlRef = useRef(null);

  // State to keep track of whether the Command key is pressed
  // const [isCommandPressed, setIsCommandPressed] = useState(false);
  const [mode, setMode] = useState(MODES.NONE);

  // const [actionControls, setActionControls] = useState({
  //   pan: mode === MODES.PAN,
  //   move: mode === MODES.MOVE,
  //   select: mode === MODES.SELECT,
  // });

  const actionControls = useMemo(
    () => ({
      pan: mode === MODES.PAN,
      move: mode === MODES.MOVE,
      select: mode === MODES.SELECT,
      rotation: false,
      zoom: false,
    }),
    [mode],
  );

  //Zoom
  const [zoomvalue, setZoomvalue] = useState(150);

  //State to store all bins in all racks
  const [products, setProducts] = useState([]);

  //State to modal open
  const [openModal, setActiveModal] = useState(false);
  //State to store modalInfo
  const [modalContent, setModalContent] = useState(null);

  const productsRef = useRef([]);

  const urlParams = useQueryParams();

  //Cameria View
  const [cameraView, setCameraView] = useState(null);

  //Using Ref value for storing selected element to view in 3D, to prevent rerenders
  const selectedElementRef = useRef(null);
  const [showObjectInfoPanel, setShowObjectInfoPanel] = useState(false);

  const [selection, setSelection] = useState(null);

  //Warehouse Selected Info
  const [currentWarehouseInfo, setCurrentWarehouseInfo] = useState(null);

  const [scaleFactorValue, setScaleFactorValue] = useState(0.01);

  // 1. ADD NEW STATE to track scene readiness
  const [isSceneReady, setIsSceneReady] = useState(false);

  const toggleMode = useCallback((nextMode) => {
    setMode((prev) => (prev === nextMode ? MODES.NONE : nextMode));
  }, []);

  const isWareHouseInventoryRoute = window.location.pathname.split("/").some((path) => ["warehouse-inventory"].includes(path));
  const isSELocationHighlightRoute = window.location.pathname.split("/").some((path) => ["element-finder"].includes(path));

  function calculate3DCoordinatesForRack(rack) {
    const scaleFactor = currentWarehouseInfo?.scale3D;
    // const scaleFactor = 0.01;

    // check the longer side of the rack and align the bins in that direction
    const rackHeight = rack.y2 - rack.y1;
    const _rackWidth = rack.x2 - rack.x1;

    let transformedBins = [];
    if (rack?.bins?.length) {
      transformedBins = (rack.bins || [])
        .filter((bin) => bin.isBinDeleted === false)
        .map((bin) => {
          // Assuming z-axis is depth (coming out of the screen)

          const rackX = rack.x1;
          const rackZ = rack.y1;
          const rackY = 0;
          const rackWidth = rack.y2 - rack.y1;

          const x = rackX + bin.x;
          const y = (rackY + bin.y) * -1;
          const z = rackZ;

          // calculate size
          const size = [bin.width * scaleFactor, bin.height * scaleFactor, rackWidth * scaleFactor];
          const position = [x * scaleFactor + size[0] / 2, y * scaleFactor - size[1] / 2, z * scaleFactor + size[2] / 2];
          // if the rackHeight is longer than the rackWidth
          if (rackHeight > _rackWidth) {
            const x = rackX;
            const y = (rackY + bin.y) * -1;
            const z = rackZ + bin.x;
            // adjust position and size
            const size = [_rackWidth * scaleFactor, bin.height * scaleFactor, bin.width * scaleFactor];
            const position = [x * scaleFactor + size[0] / 2, y * scaleFactor - size[1] / 2, z * scaleFactor + size[2] / 2];
            return {
              ...bin,
              showCube: true,
              showLabel: true,
              size,
              position,
              opacity: 1,
              edgeColor: "red",
              color: rack.color,
              objectType: "bin",
              facingDirection: rack.facingDirection,
              unitType: rack.unitType,
            };
          }

          return {
            ...bin,
            showCube: true,
            showLabel: true,
            color: rack.color,
            size,
            position,
            opacity: 1,
            edgeColor: "red",
            objectType: "bin",
            facingDirection: rack.facingDirection,
            unitType: rack.unitType,
          };
        });
    }
    // const updatedRack = calculate3DCoordinatesForArea(rack);
    // return { ...updatedRack, bins: transformedBins, rotation: rack.rotation || 0 };

    // const updatedRack = calculate3DCoordinatesForArea(rack);

    // // Calculate rack center
    // const rackCenterX = (rack.x1 + rack.x2) / 2;
    // const rackCenterY = (rack.y1 + rack.y2) / 2;
    // const rackCenterZ = 0;

    // // Convert bin positions to be relative to the rack center
    // const binsWithLocalPos = transformedBins.map(bin => {
    //   const localPos = [
    //     bin.position[0] - rackCenterX * scaleFactor,
    //     bin.position[1],
    //     bin.position[2] - rackCenterY * scaleFactor
    //   ];
    //   return { ...bin, position: localPos };
    // });

    // return {
    //   ...updatedRack,
    //   bins: binsWithLocalPos,
    //   position: [rackCenterX * scaleFactor, 0, rackCenterY * scaleFactor],
    //   rotation: rack.rotation || 0
    // };

    const updatedRack = calculate3DCoordinatesForArea(rack);

    // compute rack center once
    const rackCenterX = (rack.x1 + rack.x2) / 2;
    const rackCenterY = (rack.y1 + rack.y2) / 2;
    const rackCenterZ = 0;

    let binsWithAdjustedPos = transformedBins;

    // Apply local conversion **only** for rotated racks
    if (rack.rotation && rack.rotation !== 0) {
      binsWithAdjustedPos = transformedBins.map((bin) => {
        const localPos = [bin.position[0] - rackCenterX * scaleFactor, bin.position[1], bin.position[2] - rackCenterY * scaleFactor];
        return { ...bin, position: localPos };
      });
    }

    // always set rack’s world position (center) and rotation
    return {
      ...updatedRack,
      bins: binsWithAdjustedPos,
      position: [rackCenterX * scaleFactor, 0, rackCenterY * scaleFactor],
      rotation: rack.rotation || 0,
    };
  }

  function calculate3DCoordinatesForArea(element) {
    let scaleFactor = currentWarehouseInfo?.scale3D || 0.01;

    const X = element.x1;
    const Z = element.y1;
    const Y = 0.3;
    const width = element.x2 - element.x1;
    const height = element.y2 - element.y1;

    const size = [width * scaleFactor, 0, height * scaleFactor];
    const position = [X * scaleFactor + size[0] / 2, Y, Z * scaleFactor + size[2] / 2];

    if (element.subAreas?.length > 0) {
      element.subAreas = element.subAreas.map((subArea) => calculate3DCoordinatesForArea(subArea));
    }

    return { ...element, position, size, rotation: element.rotation || 0 };
  }

  function calculate3DCoordinatesForZone(element) {
    let scaleFactor = currentWarehouseInfo?.scale3D || 0.01;

    const X = element.x1;
    const Z = element.y1;
    const Y = 0.2;
    const width = element.x2 - element.x1;
    const height = element.y2 - element.y1;

    const size = [width * scaleFactor, 0, height * scaleFactor];
    const position = [X * scaleFactor + size[0] / 2, Y, Z * scaleFactor + size[2] / 2];

    return { ...element, position, size, rotation: element.rotation || 0 };
  }

  function calculate3DCoordinatesForDock(element) {
    const scaleFactor = currentWarehouseInfo?.scale3D || 0.01;
    const X = element.x1;
    const Z = element.y1;
    const Y = 0;

    const width = element.x2 - element.x1;
    const height = element.y2 - element.y1;

    const size = [width * scaleFactor, 2 * (scaleFactor * 100), height * scaleFactor];
    const position = [X * scaleFactor + size[0] / 2, Y * scaleFactor + size[1] / 2, Z * scaleFactor + size[2] / 2];

    return { ...element, position, size, rotation: element.rotation || 0 };
  }

  function calculate3DCoordinatesForWall(element) {
    const scaleFactor = currentWarehouseInfo?.scale3D || 0.01;
    const thickness = 0.2; // Or whatever thickness (in unscaled units)
    const height = getWallHeight(element);

    // Wall segment vector in 2D (x,y)
    const dx = element.x2 - element.x1;
    const dz = element.y2 - element.y1;
    const length = Math.sqrt(dx * dx + dz * dz);

    // 3D wall size: [length, height, thickness]
    const size = [length * scaleFactor, height * scaleFactor, thickness];

    // Center point of the wall segment
    const centerX = (element.x1 + element.x2) / 2;
    const centerZ = (element.y1 + element.y2) / 2;
    const Y = 0;

    const position = [centerX * scaleFactor, Y * scaleFactor + size[1] / 2, centerZ * scaleFactor];

    // Angle of wall in 2D (y-axis rotation in 3D)
    const rotationY = -Math.atan2(dz, dx); // Negative for Three.js...(z forward, x right)

    return {
      ...element,
      position,
      size,
      rotation: rotationY, // In RADIANS for Three.js!
    };
  }

  // Fetch Active Layout
  const getActiveLayout = async () => {
    try {
      const data = await getWarehouseLayout(urlParams.layoutId);
      const scale3D = data.scale3D || 0.01;
      setCurrentWarehouseInfo({ ...data, scale3D });
    } catch (error) {
      console.log("error", error);
      toast.error("Error while fetching warehouse layout", { autoClose: 3000 });
    }
  };

  useEffect(() => {
    getActiveLayout();
  }, []);

  useLayoutEffect(() => {
    if (!currentWarehouseInfo || !state.elements) return;

    setIsSceneReady(false);

    // For reset the value of globalShowLocation to null, to prevent global-show-location event to emit again in useEffect for isSceneReady
    if (state.globalShowLocation && isSceneReady) {
      dispatch({ type: "SET_GLOBAL_SHOW_LOCATION", payload: null });
    }

    let newElements = [];

    for (let i = 0; i < state.elements.length; i++) {
      let element = state.elements[i];
      if (element.objectType === "rack") {
        let _rack = calculate3DCoordinatesForRack(element);
        newElements.push(_rack);
        newElements.push({ ..._rack, objectType: "rackSpace", showRackLabel: true }); // calculate3DCoordinatesForRack(element);
      }

      if (element.objectType === "area") {
        let _area = calculate3DCoordinatesForArea(element);

        const _subAreas = [];
        if (element?.subAreas?.length) {
          let allSubAreas = element?.subAreas?.flatMap((area) => area);
          for (let i = 0; i < allSubAreas?.length; i++) {
            _subAreas.push(calculate3DCoordinatesForArea(allSubAreas[i]));
          }
        }

        newElements.push({ ..._area, subAreas: _subAreas });
      }

      if (element.objectType === "zone") {
        newElements.push(calculate3DCoordinatesForZone(element));
      }

      if (element.objectType === "wall") {
        newElements.push(calculate3DCoordinatesForWall(element));
      }

      if (element.objectType === "dock") {
        newElements.push(calculate3DCoordinatesForDock(element));
      }
    }
    newElements = newElements.filter((element) => element !== null);

    const products = [];
    newElements.forEach((element) => {
      if (element.objectType === "rack") {
        products.push(...element.bins);
      } else if (element.objectType === "rackSpace") {
        products.push(element);
      } else if (element.objectType === "area") {
        products.push(element);
      }
    });

    productsRef.current = products;
    setProducts(products);
    setObjects(newElements);
    setPreviewObjects(newElements);
  }, [state.elements, currentWarehouseInfo]);

  // USE EFFECT FOR SCENE READY CONFIRMATION
  useEffect(() => {
    // Only execute the IF Block, if the scene is ready and there are objects to show.
    if (isSceneReady && objects.length > 0) {
      // toast.success("3D Warehouse View is Ready!", { autoClose: 2000, position: "bottom-center" });
      dispatch({ type: "HIDE_GLOBAL_LOADER" });

      if (state.globalShowLocation) {
        eventEmiterInstance.emit("global-show-location", state.globalShowLocation);
      }
    }
  }, [isSceneReady, objects.length, state.globalShowLocation]);

  useEffect(() => {
    productsRef.current = products;
  }, [products]);
  //Find Selected Bin In 3D

  const fetchInventoryProducts = async (payload) => {
    const products_response = await getProducts();
    let Inventory_Products = products_response?.data;
    return { Inventory_Products };
  };

  //custom camera view based on the selected cube
  const customCameraView = (cube) => {
    let cubePosition = [...cube.position];
    //Checking the Direction of the cube
    if (cube.facingDirection === "SN" || cube.facingDirection === "NS") {
      cubePosition[0] = cubePosition[0] > 0 ? cubePosition[0] + 3 : cubePosition[0] - 3;
      cubePosition[2] = cubePosition[2] > 0 ? cubePosition[2] + 5 : cubePosition[2] - 5;
    } else {
      cubePosition[0] = cubePosition[0] > 0 ? cubePosition[0] + 5 : cubePosition[0] + 5;
      cubePosition[2] = cubePosition[2] > 0 ? cubePosition[2] : cubePosition[2] - 5;
    }
    let cameraPosition = [cubePosition[0], cubePosition[1] + 2, cubePosition[2]];
    setCameraView(cameraPosition);
  };

  function updateCube() {
    setAreaLocationsToHighlight({});
  }

  // Memoize the binInstances derived from objects
  const binInstances = useMemo(() => {
    const instancesMap = new Map();
    objects.forEach((obj) => {
      // Check if obj and obj.bins exist to prevent errors
      if (obj && obj.objectType === "rack" && obj.bins?.length) {
        // Use a stable key like obj.id if available, otherwise obj.code
        const key = obj.id || obj.code;
        if (!instancesMap.has(key)) {
          // Directly use the obj.bins array reference assuming it's stable
          // when only parent state changes. If obj.bins itself is unstable
          // from the calculation, that needs fixing too.
          instancesMap.set(key, {
            instances: obj.bins,
            rackInfo: obj,
          });
        } else {
          // Handle potential duplicate keys if necessary, though ideally keys are unique
          // If keys can duplicate, you might need to merge instances or adjust logic
          console.warn(`Duplicate key found for rack instance: ${key}`);
          // Example: Merge bins (if structure allows and makes sense)
          // instancesMap.get(key).instances.push(...obj.bins);
        }
      }
    });
    return instancesMap;
  }, [objects]); // Only recalculate when the main 'objects' state changes

  const racksInstancesIds = objects.flatMap((obj) => (obj.objectType === "rack" && obj.bins?.length ? [obj.id] : []));
  let racksInstances = racksInstancesIds;

  // Declare racksInstances outside InstancedCube component to track IDs across multiple calls (one per rack).
  // Used to find the last rack instance and trigger highlightAreas.
  const getRemainingRackIds = (id) => {
    //Removing rackId from racksInstances to Identify the last rack
    const remainingRacksIds = racksInstances.filter((rackId) => rackId !== id);
    racksInstances = remainingRacksIds;

    return remainingRacksIds;
  };

  // Events like hideExternalRacks/resetAllRacks trigger handlers on all racks, which filters rackInstancesIds.
  // By the end, only the last rack's ID remains. To ensure subsequent calls of these same events
  // operate on the complete list, setRackInstancesIds() is called to restore the original IDs.
  const resetRackInstances = () => {
    racksInstances = racksInstancesIds;
  };

  // const renderObjects = () => {
  //   const objectsToRender = [];

  //   objects.forEach((obj, index) => {
  //     switch (obj.objectType) {
  //       case "rack":
  //         objectsToRender.push(<Rack key={obj.id || index} obj={obj} warehouseInfo={currentWarehouseInfo} />);
  //         break;

  //       case "area":
  //         objectsToRender.push(
  //           <AreaWrapper
  //             key={obj.id || index}
  //             obj={obj}
  //             areaLocationsToHighlight={areaLocationsToHighlight}
  //             isWareHouseInventoryRoute={isWareHouseInventoryRoute}
  //           />
  //         );
  //         break;

  //       case "zone":
  //         const rotationY = obj.rotation ? (-obj.rotation * Math.PI) / 180 : 0;
  //         objectsToRender.push(
  //           <group key={obj.id || index} position={obj.position} rotation={[0, rotationY, 0]}>
  //             <Zone obj={{ ...obj, position: [0, 0, 0] }} transparent opacity={0.25} />
  //           </group>
  //         );
  //         break;

  //       case "wall":
  //         objectsToRender.push(<ModernWall key={obj.id || index} obj={obj} />);
  //         break;
  //       case "dock":
  //         objectsToRender.push(<ModernDock key={obj.id || index} obj={obj} />);
  //         break;

  //       default:
  //         break;
  //     }
  //   });

  //   // Create instanced meshes for each group of similar bins
  //   binInstances.forEach((group, key) => {
  //     objectsToRender.push(
  //       <InstancedCube
  //         key={key}
  //         instances={group.instances}
  //         showLabels={true}
  //         rackInfo={group.rackInfo}
  //         rackId={group.rackInfo.id}
  //         selection={selection}
  //         onSelection={setSelection}
  //         isWareHouseInventoryRoute={isWareHouseInventoryRoute}
  //         getRemainingRackIds={getRemainingRackIds}
  //         resetRackInstances={resetRackInstances}
  //         highlightAreas={highlightAreas}
  //         updateCube={updateCube}
  //         rotation={group.rackInfo.rotation || 0} // Pass rotation to component
  //       />
  //     );
  //   });

  //   return objectsToRender;
  // };

  //   const renderObjects = () => {
  //     const objectsToRender = [];

  //     objects.forEach((obj, index) => {
  //       let renderedObject = null;

  //       switch (obj.objectType) {
  //         case "rack":
  //           renderedObject = (
  //             <Rack key={obj.id || index} obj={obj} warehouseInfo={currentWarehouseInfo} />
  //           );
  //           break;

  //         case "area":
  //           renderedObject = (
  //             <AreaWrapper
  //               key={obj.id || index}
  //               obj={obj}
  //               areaLocationsToHighlight={areaLocationsToHighlight}
  //               isWareHouseInventoryRoute={isWareHouseInventoryRoute}
  //             />
  //           );
  //           break;

  //         case "zone":
  //           const rotationY = obj.rotation ? (-obj.rotation * Math.PI) / 180 : 0;
  //           renderedObject = (
  //             <group key={obj.id || index} position={obj.position} rotation={[0, rotationY, 0]}>
  //               <Zone obj={{ ...obj, position: [0, 0, 0] }} transparent opacity={0.25} />
  //             </group>
  //           );
  //           break;

  //         case "wall":
  //           renderedObject = <ModernWall key={obj.id || index} obj={obj} />;
  //           break;

  //         case "dock":
  //           renderedObject = <ModernDock key={obj.id || index} obj={obj} />;
  //           break;

  //         default:
  //           break;
  //       }

  //       if (renderedObject) {
  //         // Wrap in InteractiveObject for editing features
  //         objectsToRender.push(
  //           <InteractiveObject
  //             key={obj.id || index}
  //             object={obj}
  //             editable={true}
  //             rotatable={true}
  //             copyable={true}
  //             isolatable={true}
  //             onUpdate={(updatedObj) => {
  //               updateObjectInStore(updatedObj); // your existing store update
  //               saveObjectToBackend(updatedObj); // optional backend call
  //             }}
  //           >
  //             {renderedObject}
  //           </InteractiveObject>
  //         );
  //       }
  //     });

  //     // Keep your binInstances logic as is
  //     binInstances.forEach((group, key) => {
  //       objectsToRender.push(
  //         <InteractiveObject
  //           key={`bin-${key}`}
  //           object={{
  //             ...group.rackInfo,
  //             objectType: "rack",
  //             position: group.rackInfo.position,
  //             rotation: [0, group.rackInfo.rotation || 0, 0],
  //           }}
  //           editable={true}
  //           rotatable={true}
  //           copyable={true}
  //           isolatable={true}
  //           onUpdate={(updatedObj) => {
  //             updateCube(updatedObj); // Your existing bin update logic
  //           }}
  //         >
  //           <InstancedCube
  //             instances={group.instances}
  //             showLabels={true}
  //             rackInfo={group.rackInfo}
  //             rackId={group.rackInfo.id}
  //             selection={selection}
  //             onSelection={setSelection}
  //             isWareHouseInventoryRoute={isWareHouseInventoryRoute}
  //             getRemainingRackIds={getRemainingRackIds}
  //             resetRackInstances={resetRackInstances}
  //             highlightAreas={highlightAreas}
  //             rotation={group.rackInfo.rotation || 0}
  //           />
  //         </InteractiveObject>
  //       );
  //     });

  //     return objectsToRender;
  // };

  useEffect(() => {
    renderObjects();
  }, [objects]);

  const [selectedObject, setSelectedObject] = useState(null);
  const [showBinDirectly, setShowBinDirectly] = useState(false);

  const setObjectSelection = (object) => {
    setShowBinDirectly(false);
    setSelectedObject(object);
    dispatch({ type: "SELECTED_ELEMENT", payload: object });
  };

  const showBinDetailsDirectly = (bin) => {
    try {
      const { rackId } = bin;

      const rack = objects.find((obj) => obj.id === rackId);
      setObjectSelection(rack);
      setShowObjectInfoPanel(true);
      if (mode !== "move") {
        setShowBinDirectly(bin);
      }
    } catch (error) {
      setShowBinDirectly(false);
      setSelectedObject(null);
      dispatch({ type: "SELECTED_ELEMENT", payload: null });
      console.log("Error in showBinDetailsDirectly", error);
    }
  };

  const renderObjects = () => {
    const objectsToRender = [];

    objects.forEach((obj, index) => {
      let renderedObject = null;

      switch (obj.objectType) {
        case "rack":
          renderedObject = (
            <InteractiveObject
              key={`${obj.id || index}-${obj.width}-${obj.length}-${obj.height}`}
              object={obj}
              editable={!isWareHouseInventoryRoute && !isSELocationHighlightRoute && mode === "move"}
              scaleFactor={currentWarehouseInfo?.scale3D || 0.01}
              selectedElementRef={selectedElementRef}
              showObjectInfoPanel={showObjectInfoPanel}
              setShowObjectInfoPanel={setShowObjectInfoPanel}
              onOpenContextMenu={openContextMenu}
              onSelect={setObjectSelection}
            >
              <Rack obj={obj} warehouseInfo={currentWarehouseInfo} />
            </InteractiveObject>
          );
          break;

        case "area":
          renderedObject = (
            <InteractiveObject
              showObjectInfoPanel={showObjectInfoPanel}
              setShowObjectInfoPanel={setShowObjectInfoPanel}
              key={`${obj.id || index}-${obj.width}-${obj.length}-${obj.height}`}
              object={obj}
              editable={false}
              scaleFactor={currentWarehouseInfo?.scale3D || 0.01}
              selectedElementRef={selectedElementRef}
              onOpenContextMenu={openContextMenu}
              onSelect={setObjectSelection}
            >
              <AreaWrapper obj={obj} areaLocationsToHighlight={areaLocationsToHighlight} isWareHouseInventoryRoute={isWareHouseInventoryRoute} />
            </InteractiveObject>
          );
          break;

        case "zone":
          const rotationY = obj.rotation ? (-obj.rotation * Math.PI) / 180 : 0;
          renderedObject = (
            <InteractiveObject
              showObjectInfoPanel={showObjectInfoPanel}
              setShowObjectInfoPanel={setShowObjectInfoPanel}
              key={`${obj.id || index}-${obj.width}-${obj.length}-${obj.height}`}
              object={obj}
              editable={!isWareHouseInventoryRoute && !isSELocationHighlightRoute && mode === "move"}
              scaleFactor={currentWarehouseInfo?.scale3D || 0.01}
              selectedElementRef={selectedElementRef}
              onOpenContextMenu={openContextMenu}
              onSelect={setObjectSelection}
            >
              <group rotation={[0, rotationY, 0]}>
                <Zone obj={{ ...obj }} transparent opacity={0.25} />
              </group>
            </InteractiveObject>
          );
          break;

        case "wall":
          renderedObject = (
            <InteractiveObject
              showObjectInfoPanel={showObjectInfoPanel}
              setShowObjectInfoPanel={setShowObjectInfoPanel}
              key={`${obj.id || index}-${obj.width}-${obj.length}-${obj.height}`}
              object={obj}
              editable={!isWareHouseInventoryRoute && !isSELocationHighlightRoute && mode === "move"}
              scaleFactor={currentWarehouseInfo?.scale3D || 0.01}
              selectedElementRef={selectedElementRef}
              onOpenContextMenu={openContextMenu}
              onSelect={setObjectSelection}
            >
              <ModernWall obj={obj} shouldRender={!isSELocationHighlightRoute} />
            </InteractiveObject>
          );
          break;

        case "dock":
          renderedObject = (
            <InteractiveObject
              showObjectInfoPanel={showObjectInfoPanel}
              setShowObjectInfoPanel={setShowObjectInfoPanel}
              key={`${obj.id || index}-${obj.width}-${obj.length}-${obj.height}`}
              object={obj}
              editable={!isWareHouseInventoryRoute && !isSELocationHighlightRoute && mode === "move"}
              scaleFactor={currentWarehouseInfo?.scale3D || 0.01}
              selectedElementRef={selectedElementRef}
              onOpenContextMenu={openContextMenu}
              onSelect={setObjectSelection}
            >
              <ModernDock obj={obj} />
            </InteractiveObject>
          );
          break;

        default:
          break;
      }

      if (renderedObject) {
        objectsToRender.push(renderedObject);
      }
    });

    // Handle bin instances separately WITHOUT InteractiveObject wrapper
    binInstances.forEach((group, key) => {
      objectsToRender.push(
        <InstancedCube
          key={key}
          instances={group.instances}
          showLabels={true}
          rackInfo={group.rackInfo}
          rackId={group.rackInfo.id}
          selection={selection}
          onSelection={setSelection}
          isWareHouseInventoryRoute={isWareHouseInventoryRoute}
          isSELocationHighlightRoute={isSELocationHighlightRoute}
          getRemainingRackIds={getRemainingRackIds}
          resetRackInstances={resetRackInstances}
          highlightAreas={highlightAreas}
          rotation={group.rackInfo.rotation || 0}
          showBinDetailsDirectly={showBinDetailsDirectly}
          onOpenContextMenu={openContextMenu}
          onUpdate={(updatedObj) => {
            updateCube(updatedObj); // Your existing bin update logic
          }}
          updateCube={updateCube}
        />,
      );
    });

    return objectsToRender;
  };

  useImperativeHandle(ref, () => {
    return {
      setLoading,
      selectedElementRef,
      viewCategoryWiseProducts,
      updateCube,
    };
  });

  const highlightAreas = (locationCodes, color) => {
    setAreaLocationsToHighlight({ primaryColor: color, areaLocations: [...locationCodes] });
  };

  useEffect(() => {
    const handleShowBinInfo = (cube) => {
      setModalContent({ modalTitle: "Bin Details", isLoading: true });

      setActiveModal(true);
      fetchInventoryProducts(cube).then(({ Inventory_Products }) => {
        let { modalPayload, _docLocationLookup } = binInfo(Inventory_Products, cube, "bin");

        const modalContent = {
          modalTitle: "Bin Details",

          isLoading: false,
          modalBody: modalPayload,
          Inventory_Products,
          _docLocationLookup,
        };

        setModalContent(modalContent);
      });
    };

    const handleShowSubAreaInfo = (subArea) => {
      setModalContent({ modalTitle: "Area Details", isLoading: true });

      setActiveModal(true);
      fetchInventoryProducts().then(({ Inventory_Products }) => {
        let { modalPayload, _docLocationLookup } = binInfo(Inventory_Products, subArea, "subArea");
        const modalContent = {
          modalTitle: "Area Details",
          isLoading: false,
          modalBody: modalPayload,
          Inventory_Products,
        };
        setModalContent(modalContent);
      });
    };

    eventEmiterInstance.on("showBinInfo", handleShowBinInfo);
    eventEmiterInstance.on("showSubAreaInfo", handleShowSubAreaInfo);

    return () => {
      eventEmiterInstance.off("showBinInfo");
      eventEmiterInstance.off("showSubAreaInfo");
    };
  }, []);

  const viewCategoryWiseProducts = (doc_locations, _color) => {
    // Create a Set for O(1) lookup of doc_locations codes
    const docLocationSet = new Set(doc_locations.map((item) => item.doc_location));

    const newProducts = products.map((cube) => {
      const isLocationExists = docLocationSet.has(cube.code);

      // Check if any subBin code exists in docLocationSet
      const isSubBinExists = cube.objectType !== "rackSpace" && cube.subBins?.some((subBin) => docLocationSet.has(subBin.code));

      let opacity, color, edgeColor;

      if (isLocationExists || isSubBinExists) {
        opacity = 1;
        color = _color;
        edgeColor = "white";
      } else if (doc_locations.length) {
        opacity = 0.1;
        color = null;
        edgeColor = "white";
      } else {
        opacity = 1;
        color = null;
        edgeColor = cube.edgeColor;
      }

      // Return new cube object with updated properties for immutability (optional)
      return {
        ...cube,
        opacity,
        color,
        edgeColor,
      };
    });

    setProducts(newProducts);
  };

  const onCloseHandler = () => {
    setActiveModal(false);
    setModalContent(null);
    eventEmiterInstance.off("binMounted");
    eventEmiterInstance.off("showBinInfo");
  };

  // const onResetCameraView = () => {
  //   //Reset ActionControls
  //   setActionControls({ pan: false });
  //   setZoomvalue(100);
  //   camera?.current.position.set(0, 55, 122);
  //   // // Reset controls target
  //   mapControlRef.current.target.set(0, 0, 0);
  //   mapControlRef.current.update(); // Update controls to apply changes
  //   // //SET ORIGIN
  //   camera.current.updateProjectionMatrix();
  // };

  const onResetCameraView = () => {
    // Reset ActionControls
    // setActionControls({ pan: false });
    setZoomvalue(100); // Reset zoom to 100%

    // Reset camera to default position (100% zoom)
    if (camera?.current) {
      camera.current.position.set(0, 55, 122);

      // Reset controls target
      if (mapControlRef.current) {
        mapControlRef.current.target.set(0, 0, 0);
        mapControlRef.current.update();
      }

      // Update projection matrix
      camera.current.updateProjectionMatrix();
    }

    setMode(MODES.NONE);
  };

  const onPan = () => toggleMode(MODES.PAN);
  const onMove = () => toggleMode(MODES.MOVE);
  const onSelect = () => toggleMode(MODES.SELECT);

  // const onPan = () => {
  //   let _actionControls = { ...actionControls };
  //   console.log("actionControls", actionControls);

  //   if (actionControls["pan"]) {
  //     _actionControls = { rotation: false, pan: false, zoom: false, move: false };
  //   } else {
  //     _actionControls = { rotation: false, pan: true, zoom: false, move: false };
  //   }
  //   setActionControls(_actionControls);
  //   setMode("pan");
  // };

  // const onMoveSelect = () => {
  //   let _actionControls = { ...actionControls };
  //   console.log("actionControls", actionControls);

  //   if (actionControls["move"]) {
  //     _actionControls = { rotation: false, pan: false, zoom: false, move: false };
  //   } else {
  //     _actionControls = { rotation: false, pan: false, zoom: false, move: true };
  //   }
  //   setActionControls(_actionControls);
  //   // Now the editing in the interactive mode should be enabled
  //   setMode("move");
  // };

  // const onSelect = () => {
  //   let _actionControls = { ...actionControls };
  //   console.log("actionControls", actionControls);

  //   if (actionControls["select"]) {
  //     _actionControls = { rotation: false, pan: false, zoom: false, select: false, move: false };
  //   } else {
  //     _actionControls = { rotation: false, pan: false, zoom: false, select: true, move: false };
  //   }
  //   setActionControls(_actionControls);
  //   // Now the editing in the interactive mode should be enabled
  //   setMode("select");
  // };

  const manipulateCameraPosition = (direction) => {
    if (!camera?.current) return;

    // Preset positions for each direction (adjust to your scene scale)
    const positions = {
      North: new THREE.Vector3(camera.current.position.x, camera.current.position.y, -Math.abs(camera.current.position.z)),
      South: new THREE.Vector3(-1, camera.current.position.y, 300),
      East: new THREE.Vector3(300, camera.current.position.y, 0),
      West: new THREE.Vector3(-300, camera.current.position.y, 0),
    };

    // Desired target to look at (center of scene or your target object)
    const target = new THREE.Vector3(0, 0, 0);

    const targetPosition = positions[direction];
    if (targetPosition) {
      camera.current.position.copy(targetPosition);
      camera.current.lookAt(target);
      camera.current.updateProjectionMatrix();
    } else {
      console.warn(`Invalid direction: ${direction}`);
    }
  };

  const globalShowLocation = (payload) => {
    const { binLocation } = payload;
    const storageLocationCodes = binLocation?.split(",").map((item) => item.replace(/"/g, "").trim()) || [];
    highlightLocations(storageLocationCodes);
  };

  useEffect(() => {
    const _updateCube = () => {
      // reset the camera position
      onResetCameraView();
      // updateCube();
      //  highlightLocations([]);
    };
    eventEmiterInstance.on("global-show-location", globalShowLocation);
    eventEmiterInstance.on("global-hide-location", _updateCube);

    return () => {
      eventEmiterInstance.off("global-show-location", globalShowLocation);
      eventEmiterInstance.off("global-hide-location", _updateCube);
    };
  }, [products]);

  const handleScale3dChange = async (scaleValue) => {
    const payload = { ...currentWarehouseInfo, scale3D: scaleValue };
    setCurrentWarehouseInfo(payload);
    setScaleFactorLoading(true);
    debouncedUpdateLayout(currentWarehouseInfo.id, payload);
  };

  const debouncedUpdateLayout = useMemo(
    () =>
      debounce(async (id, payload) => {
        try {
          await updateLayout(id, payload);
        } catch (error) {
          toast.error("Error while updating warehouse layout");
        } finally {
          setScaleFactorLoading(false);
        }
      }, 1000),
    [],
  );

  const handleZoomChange = (zoomValue) => {
    // Update zoom state
    setZoomvalue(zoomValue);

    // Calculate zoom multiplier based on zoom percentage
    // When zoom is 100%, multiplier is 1
    // When zoom is 50%, multiplier is 2 (zoom out)
    // When zoom is 140%, multiplier is 0.714 (zoom in)
    const zoomMultiplier = 100 / zoomValue;

    if (camera?.current) {
      // Get the current distance from origin
      const currentPosition = camera.current.position;
      const distance = Math.sqrt(currentPosition.x ** 2 + currentPosition.y ** 2 + currentPosition.z ** 2);

      // Get normalized direction vector
      const direction = new THREE.Vector3(currentPosition.x / distance, currentPosition.y / distance, currentPosition.z / distance);

      // Base distance for 100% zoom
      const baseDistance = 122; // Match your reset position

      // Calculate new position based on zoom
      const newDistance = baseDistance * zoomMultiplier;

      // Set new position along the same direction
      camera.current.position.set(direction.x * newDistance, direction.y * newDistance, direction.z * newDistance);

      // Update the camera
      camera.current.updateProjectionMatrix();

      // If using OrbitControls/MapControls, update them too
      if (mapControlRef.current) {
        mapControlRef.current.update();
      }
    }
  };

  const [rackFrontViewFormData, setRackFrontViewFormData] = useState(null);

  const [showBinConfigModal, setShowBinConfigModal] = useState(false);
  useEffect(() => {
    if (selectedElementRef?.current && showBinConfigModal) {
      console.log("selectedElementRef?.current", selectedElementRef?.current);

      setRackFrontViewFormData(getDataForRackFrontView());
    }
  }, [selectedElementRef?.current, showBinConfigModal]);
  const getDataForRackFrontView = () => {
    console.log("selectedElementRef?.current", selectedElementRef?.current);

    return {
      rackName: selectedElementRef?.current?.name,
      rackCode: selectedElementRef?.current?.code,
      rackX: selectedElementRef?.current?.positionX,
      rackY: selectedElementRef?.current?.positionY,
      rackWidth: selectedElementRef?.current?.width,
      rackHeight: selectedElementRef?.current?.height,
      wallHeight: null,
      unitType: selectedElementRef?.current?.unitType || UNIT_TYPES.inch,
      maxCapacity: selectedElementRef?.current?.maxCapacity || null,
    };
  };

  const handleShelfToggeler = () => {
    setShowBinConfigModal((prev) => !prev);
    dispatch({ type: "RE_SET_ELEMENTS", payload: [] });
  };
  const [menuState, setMenuState] = useState({
    visible: false,
    object: null,
    screen: null, // { x, y } in client pixels
  });

  const containerRef = useRef(null); // wrapper for canvas + overlay
  const menuRef = useRef(null);

  const openContextMenu = useCallback(({ object, screen }) => {
    setMenuState({ visible: true, object, screen });
  }, []);

  const closeContextMenu = useCallback(() => {
    setMenuState({ visible: false, object: null, screen: null });
  }, []);

  // Close menu on outside click / ESC
  useEffect(() => {
    function onPointerDown(e) {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      // clicked outside menu
      closeContextMenu();
    }
    function onKey(e) {
      if (e.key === "Escape") closeContextMenu();
    }
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [closeContextMenu]);

  // Close on scroll/resize to avoid stale positions. You can instead reposition if you want.
  useEffect(() => {
    function handler() {
      closeContextMenu();
    }
    window.addEventListener("scroll", handler, true); // capture so scroll anywhere
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [closeContextMenu]);

  // Clamp a menu position to viewport so it stays visible
  const getClampedPosition = (screen) => {
    if (!screen) return { left: 0, top: 0 };

    const containerRect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

    const menuWidth = 240;
    const menuHeight = 350;
    const padding = 8;
    const POINTER_OFFSET = 8;

    // Convert client -> container local
    let left = screen.x - containerRect.left;
    let top = screen.y - containerRect.top;

    // clamp to container size (not viewport)
    const vw = containerRect.width;
    const vh = containerRect.height;

    if (left + menuWidth > vw - padding) left = vw - menuWidth - padding;
    if (top + menuHeight > vh - padding) top = vh - menuHeight - padding;
    left = Math.max(padding, left);
    top = Math.max(padding, top);

    // If your container is positioned with CSS (position: relative),
    // these left/top values can be used directly for absolute children.
    return { left, top };
  };

  const { left = 0, top = 0 } = menuState.screen ? getClampedPosition(menuState.screen) : {};

  // JIRA(SEP-1900)
  // Function handles ground clicks
  const onGroundClick = () => {
    // When user clicks on the ground, close the object info panel
    if (showObjectInfoPanel) setShowObjectInfoPanel(false);
    // When user clicks on the ground, close the bin config modal
    if (showBinConfigModal) setShowBinConfigModal(false);
    // When user clicks on the ground, close the selected object
    if (selectedObject) setSelectedObject(null);
    // When user clicks on the ground, close the selected element
    if (selectedElementRef?.current) selectedElementRef.current = null;
  };

  const handlePropertiesClick = (object) => {
    setSelectedObject(object);
    setShowBinDirectly(false);
    dispatch({ type: "SELECTED_ELEMENT", payload: object });
    setShowObjectInfoPanel(true);
  };

  const moveCamera = (forward, right) => {
    const controls = mapControlRef.current;
    if (!controls) return;

    const camera = controls.object;

    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);

    // Prevent vertical movement
    direction.y = 0;
    direction.normalize();

    const rightVector = new THREE.Vector3().crossVectors(direction, camera.up).normalize();

    const speed = controls.getDistance() * 0.05; // zoom-aware

    const movement = new THREE.Vector3().addScaledVector(direction, forward * speed).addScaledVector(rightVector, right * speed);

    camera.position.add(movement);
    controls.target.add(movement);

    controls.update();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowUp":
          moveCamera(1, 0); // forward
          break;
        case "ArrowDown":
          moveCamera(-1, 0); // backward
          break;
        case "ArrowLeft":
          moveCamera(0, -1); // left
          break;
        case "ArrowRight":
          moveCamera(0, 1); // right
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      {header}
      <div className={`visualizer-container ${fullscreen && "fullscreen"}`}>
        <div className="visualizer-area" ref={containerRef}>
          <Canvas
            onMouseDown={onGroundClick}
            frameloop="demand"
            gl={{
              pixelRatio: window.devicePixelRatio,
              logarithmicDepthBuffer: true,
            }}
            camera={{ position: [20, 40, 20], fov: 45 }}
            shadows
          >
            <color attach="background" args={["#ca1515e3"]} />

            <axesHelper args={[5]} />
            <ambientLight intensity={0.85} />
            <directionalLight position={[150, 150, 150]} intensity={1} />

            <Camera cameraView={cameraView} setCamera={setCamera} />
            {renderObjects()}
            {/* <OrbitControls enablePan={false} enableZoom={true} minPolarAngle={Math.PI / 4} maxPolarAngle={(3 * Math.PI) / 4} /> */}
            {/* Panning controls for camera */}

            {/* 3. CONDITIONALLY RENDER THE NOTIFIER */}
            {/* Render this only when objects are loaded but the scene isn't confirmed ready yet. */}
            {!isSceneReady && objects.length > 0 && <SceneReadyNotifier onReady={() => setIsSceneReady(true)} />}
            {/* The below is for debugging, should turn on only in dev mode*/}
            {/* <Stats  /> */}

            <ambientLight intensity={0.8} />
            <directionalLight position={[5, 8, 5]} intensity={1.0} castShadow shadow-mapSize={[2048, 2048]} />
            {/* <directionalLight position={[-5, 8, -5]} intensity={0.5} /> */}
            {/* <directionalLight position={[0, 10, 0]} intensity={0.3} /> */}
            <hemisphereLight intensity={0.7} groundColor="#dc3f3f" position={[0, 50, 0]} />
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]} receiveShadow>
              <planeGeometry args={[500, 500]} />
              <meshStandardMaterial color="#838080" />
            </mesh>
            <Grid infiniteGrid cellSize={1} sectionSize={3} fadeDistance={100} fadeStrength={1} position={[0, 0.1, 0]} args={[100, 100]} />

            <MapControls
              ref={mapControlRef}
              // enableRotate={!actionControls["pan"]}
              enableZoom={true}
              // enablePan={actionControls["pan"]}
              enableDamping={false}
              mouseButtons={{
                LEFT: actionControls["pan"] ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE,
                // MIDDLE: THREE.MOUSE.ROTATE,
                RIGHT: THREE.MOUSE.PAN,
              }}
            />
            {/* <gridHelper args={[1050, 50, "rgba(239, 239, 239, 0.1)", "rgba(239, 239, 239, 0.1)"]} position={[0, -0.1, 0]} /> */}
            {/* <gridHelper args={[1050, 50, "#555", "#444"]} position={[0, -0.1, 0]} /> */}
            {/* <gridHelper args={[1050, 50, "rgba(144, 80, 80, 0.4)", "rgba(120,120,120,0.25)"]} /> */}
          </Canvas>

          {menuState.visible && menuState.screen && !isWareHouseInventoryRoute && (
            <LayoutObjectContextMenu
              object={menuState.object}
              menuRef={menuRef}
              left={left}
              top={top}
              closeMenu={closeContextMenu}
              showProperties={handlePropertiesClick}
            />
          )}
          {showObjectInfoPanel && (
            <ObjectInfoPanel
              setShowObjectInfoPanel={setShowObjectInfoPanel}
              objects={objects}
              setObjects={setObjects}
              scaleFactor={currentWarehouseInfo?.scale3D || 0.01}
              setShowBinConfigModal={setShowBinConfigModal}
              selectedObject={selectedObject}
              setSelectedObject={setSelectedObject}
              showBinDirectly={showBinDirectly}
              setShowBinDirectly={setShowBinDirectly}
              showActions={!isWareHouseInventoryRoute && !isSELocationHighlightRoute && mode !== "move"}
            />
          )}

          {showBinConfigModal && (
            <>
              {/* A popup modal to show bin config */}
              <div
                className="configure-shelf-container"
                style={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#29292ac2",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "absolute",
                  top: "0",
                  left: "0",
                  zIndex: "10",
                }}
              >
                <div
                  className="configure-shelf-wrapper"
                  style={{
                    width: "95%",
                    height: "90%",
                  }}
                >
                  <ConfigureShelf handleShelfToggeler={handleShelfToggeler} shelf={selectedObject} />
                </div>
              </div>
            </>
          )}
          <DirectionIndicator onChangeDirection={manipulateCameraPosition} />
          <CameraControls
            actionControls={actionControls}
            isWareHouseInventoryRoute={isWareHouseInventoryRoute}
            isSELocationHighlightRoute={isSELocationHighlightRoute}
            onResetCameraView={onResetCameraView}
            onPan={onPan}
            onSelect={onSelect}
            onMove={onMove}
          />
          {/* <ZoomControls camera={camera} zoomValue={zoomvalue} setZoomValue={setZoomvalue} onResetZoom={onResetCameraView} /> */}
        </div>
      </div>
      <AppModal title={modalContent?.modalTitle} isModalActive={openModal} onHandleModalClose={onCloseHandler} width={1200} loading={modalContent?.isLoading}>
        <BinDetails modalContent={modalContent} />
      </AppModal>
      <ScaleFactor onChange={handleZoomChange} loading={scaleFactorLoading} zoomValue={zoomvalue} setZoomValue={setZoomvalue} />
      {/* <ScaleFactor warehouseScaleFactor={currentWarehouseInfo?.scale3D} onChange={handleZoomChange} loading={scaleFactorLoading} /> */}
    </>
  );
});

export default Visualizer;
